import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { X } from 'lucide-react';
import api from '../../services/api';
import { debounce } from 'lodash';
import { getChoiceLayoutClass, resolveOptionValue } from '../../utils/fieldProperties';

const DEFAULT_THEME = {
  primary_color: '#7c3aed',
  secondary_color: '#14b8a6',
  background_color: '#ffffff',
  text_color: '#1e293b',
  accent_color: '#7c3aed',
  font_family: 'Plus Jakarta Sans',
  card_style: 'solid',
  gradient: null,
  page_background: '#f8fafc'
};

const FormPreview = ({ 
  fields, 
  basePrice, 
  campaignName, 
  onClose, 
  fieldRules = [], 
  pricingRules = [], 
  pricingFormula = '', 
  campaignId = null,
  theme = null,
  headerImage = null
}) => {
  const [formData, setFormData] = useState({});
  const [fieldVisibility, setFieldVisibility] = useState({});
  const [fieldRequired, setFieldRequired] = useState({});
  const [calculatedPrice, setCalculatedPrice] = useState(null);

  // Merge default theme with provided theme
  const activeTheme = { ...DEFAULT_THEME, ...theme };

  // Initialize all fields as visible
  useEffect(() => {
    const initialVisibility = {};
    fields.forEach(f => {
      initialVisibility[f.id] = true;
    });
    setFieldVisibility(initialVisibility);
  }, [fields]);

  // Debounced function to evaluate field rules
  const evaluateRules = useCallback(
    debounce(async (values) => {
      if (fieldRules.length === 0) return;
      
      try {
        const response = await api.post('/evaluate-field-rules', {
          field_values: values,
          field_rules: fieldRules
        });
        
        if (response.data.field_visibility) {
          setFieldVisibility(prev => ({ ...prev, ...response.data.field_visibility }));
        }
        if (response.data.field_required) {
          setFieldRequired(prev => ({ ...prev, ...response.data.field_required }));
        }
      } catch (error) {
        console.error('Failed to evaluate field rules:', error);
      }
    }, 300),
    [fieldRules]
  );

  // Calculate price when form data changes
  const calculatePrice = useCallback(
    debounce(async (values) => {
      if (!campaignId) {
        // Local calculation
        let total = parseFloat(basePrice) || 0;
        fields.forEach((field) => {
          if (field.options && values[field.id]) {
            const selectedValues = Array.isArray(values[field.id])
              ? values[field.id]
              : [values[field.id]];

            field.options.forEach((option) => {
              if (selectedValues.includes(option.value)) {
                total += parseFloat(option.price_modifier) || 0;
              }
            });
          }
        });
        setCalculatedPrice({ total_price: total, base_price: basePrice, subtotal: total });
        return;
      }

      try {
        const response = await api.post(`/campaigns/${campaignId}/calculate-price`, {
          base_price: parseFloat(basePrice) || 0,
          field_values: values,
          pricing_rules: pricingRules,
          pricing_formula: pricingFormula
        });
        setCalculatedPrice(response.data);
      } catch (error) {
        console.error('Failed to calculate price:', error);
      }
    }, 300),
    [campaignId, basePrice, pricingRules, pricingFormula, fields]
  );

  // Evaluate rules and calculate price when form data changes
  useEffect(() => {
    evaluateRules(formData);
    calculatePrice(formData);
  }, [formData, evaluateRules, calculatePrice]);

  const handleChange = (fieldId, value) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const calculateTotal = () => {
    if (calculatedPrice) {
      return calculatedPrice.total_price;
    }
    
    let total = parseFloat(basePrice) || 0;

    fields.forEach((field) => {
      if (field.options && formData[field.id]) {
        const selectedValues = Array.isArray(formData[field.id])
          ? formData[field.id]
          : [formData[field.id]];

        field.options.forEach((option) => {
          if (selectedValues.includes(option.value)) {
            total += parseFloat(option.price_modifier) || 0;
          }
        });
      }
    });

    return total;
  };

  // Check if a field should be visible based on field rules
  const isFieldVisible = (fieldId) => {
    return fieldVisibility[fieldId] !== false;
  };

  // Check if a field is required (either by default or by field rules)
  const isFieldRequired = (field) => {
    return field.required || fieldRequired[field.id] === true;
  };

  const renderField = (field) => {
    // Check visibility
    if (!isFieldVisible(field.id)) {
      return null;
    }

    // New width names (small/medium/large) + legacy (full/half/third) for back-compat.
    const widthClass = {
      // New
      small:  'col-span-6',
      medium: 'col-span-8',
      large:  'col-span-12',
      // Legacy (forms saved before the rename keep rendering correctly)
      full:   'col-span-12',
      half:   'col-span-6',
      third:  'col-span-4',
    }[field.width] || 'col-span-12';

    switch (field.type) {
      case 'name':
        const nameConfig = field.name_config || {};
        const showMiddle = nameConfig.show_middle_name;
        const showLast = nameConfig.show_last_name !== false;
        
        // Calculate grid columns based on visible fields
        let gridCols = 1;
        if (showMiddle && showLast) gridCols = 3;
        else if (showLast || showMiddle) gridCols = 2;
        
        return (
          <div key={field.id} className={widthClass}>
            <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
              {field.label} {isFieldRequired(field) && <span className="text-accent">*</span>}
            </Label>
            <div className={`grid grid-cols-${gridCols} gap-3`} style={{ 
              display: 'grid', 
              gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
              gap: '0.75rem'
            }}>
              {/* First Name - Always shown */}
              <div>
                <Label className="text-slate-500 font-jakarta text-xs mb-1 block">
                  {nameConfig.first_name_label || 'First Name'}
                </Label>
                <Input
                  type="text"
                  value={formData[`${field.id}_first`] || ''}
                  onChange={(e) => handleChange(`${field.id}_first`, e.target.value)}
                  placeholder={nameConfig.first_name_placeholder || 'Enter first name'}
                  required={isFieldRequired(field)}
                  className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary"
                />
              </div>
              
              {/* Middle Name - Optional */}
              {showMiddle && (
                <div>
                  <Label className="text-slate-500 font-jakarta text-xs mb-1 block">
                    {nameConfig.middle_name_label || 'Middle Name'}
                  </Label>
                  <Input
                    type="text"
                    value={formData[`${field.id}_middle`] || ''}
                    onChange={(e) => handleChange(`${field.id}_middle`, e.target.value)}
                    placeholder={nameConfig.middle_name_placeholder || 'Enter middle name'}
                    className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary"
                  />
                </div>
              )}
              
              {/* Last Name - Optional but recommended */}
              {showLast && (
                <div>
                  <Label className="text-slate-500 font-jakarta text-xs mb-1 block">
                    {nameConfig.last_name_label || 'Last Name'}
                  </Label>
                  <Input
                    type="text"
                    value={formData[`${field.id}_last`] || ''}
                    onChange={(e) => handleChange(`${field.id}_last`, e.target.value)}
                    placeholder={nameConfig.last_name_placeholder || 'Enter last name'}
                    required={field.required}
                    className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary"
                  />
                </div>
              )}
            </div>
            {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-2">{field.help_text}</p>}
          </div>
        );

      case 'section_header':
        return (
          <div key={field.id} className="col-span-12 mb-4">
            <h3 className="text-xl font-outfit font-semibold text-slate-900 mb-2">{field.label}</h3>
            {field.help_text && <p className="text-sm text-slate-600 font-jakarta">{field.help_text}</p>}
          </div>
        );

      case 'dropdown':
        return (
          <div key={field.id} className={widthClass}>
            <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
              {field.label} {field.required && <span className="text-accent">*</span>}
            </Label>
            <select
              value={formData[field.id] || ''}
              onChange={(e) => handleChange(field.id, e.target.value)}
              required={field.required}
              className="w-full h-11 rounded-lg border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <option value="">Select an option</option>
              {field.options?.map((opt) => {
                const submitValue = resolveOptionValue(opt);
                return (
                  <option key={opt.value ?? opt.label} value={submitValue}>
                    {opt.label} {opt.price_modifier > 0 && `(+₹${opt.price_modifier})`}
                  </option>
                );
              })}
            </select>
            {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
          </div>
        );

      case 'radio':
        return (
          <div key={field.id} className={widthClass}>
            <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
              {field.label} {field.required && <span className="text-accent">*</span>}
            </Label>
            <div className={getChoiceLayoutClass(field.column_layout)}>
              {field.options?.map((opt) => {
                const submitValue = resolveOptionValue(opt);
                return (
                  <label key={opt.value ?? opt.label} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name={field.id}
                      value={submitValue}
                      checked={formData[field.id] === submitValue}
                      onChange={(e) => handleChange(field.id, e.target.value)}
                      required={field.required}
                      className="w-4 h-4 text-primary"
                    />
                    <span className="text-sm font-jakarta text-slate-700">
                      {opt.label} {opt.price_modifier > 0 && `(+₹${opt.price_modifier})`}
                    </span>
                  </label>
                );
              })}
            </div>
            {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
          </div>
        );

      case 'checkbox':
        if (!field.options || field.options.length === 0) {
          return (
            <div key={field.id} className={widthClass}>
              <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
                {field.label} {field.required && <span className="text-accent">*</span>}
              </Label>
              <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded-lg">
                ⚠️ No options configured for this checkbox field
              </p>
            </div>
          );
        }
        {
          const selected = formData[field.id] || [];
          const maxSel   = field.max_selections;
          const atMax    = maxSel && selected.length >= maxSel;
          return (
            <div key={field.id} className={widthClass}>
              <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
                {field.label} {field.required && <span className="text-accent">*</span>}
                {maxSel && (
                  <span className={`ml-2 text-xs font-normal ${atMax ? 'text-amber-600' : 'text-slate-500'}`}>
                    ({selected.length}/{maxSel} selected)
                  </span>
                )}
              </Label>
              <div className={getChoiceLayoutClass(field.column_layout)}>
                {field.options.map((opt) => {
                  const submitValue = resolveOptionValue(opt);
                  const isChecked   = selected.includes(submitValue);
                  const lockedOut   = atMax && !isChecked;
                  return (
                    <label key={opt.value ?? opt.label} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        value={submitValue}
                        checked={isChecked}
                        disabled={lockedOut}
                        onChange={(e) => {
                          const current = formData[field.id] || [];
                          const updated = e.target.checked
                            ? [...current, submitValue]
                            : current.filter((v) => v !== submitValue);
                          handleChange(field.id, updated);
                        }}
                        className="w-4 h-4 text-primary rounded"
                      />
                      <span className={`text-sm font-jakarta ${lockedOut ? 'text-slate-400' : 'text-slate-700'}`}>
                        {opt.label} {opt.price_modifier > 0 && `(+₹${opt.price_modifier})`}
                      </span>
                    </label>
                  );
                })}
              </div>
              {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
            </div>
          );
        }

      case 'terms_checkbox':
        return (
          <div key={field.id} className="col-span-12">
            <label className="flex items-start space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData[field.id] || false}
                onChange={(e) => handleChange(field.id, e.target.checked)}
                required={field.required}
                className="w-4 h-4 text-primary rounded mt-1"
              />
              <span className="text-sm font-jakarta text-slate-700">
                {field.label} {field.required && <span className="text-accent">*</span>}
              </span>
            </label>
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id} className={widthClass}>
            <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
              {field.label} {field.required && <span className="text-accent">*</span>}
            </Label>
            <textarea
              value={formData[field.id] || ''}
              onChange={(e) => handleChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
              rows={4}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
            {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
          </div>
        );

      default:
        return (
          <div key={field.id} className={widthClass}>
            <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
              {field.label} {field.required && <span className="text-accent">*</span>}
            </Label>
            <Input
              type={field.type}
              value={formData[field.id] || ''}
              onChange={(e) => handleChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
              className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary"
            />
            {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
          </div>
        );
    }
  };

  return (
    <div 
      className="min-h-screen p-8"
      style={{ 
        background: activeTheme.page_background || activeTheme.gradient || '#f8fafc',
        fontFamily: activeTheme.font_family || 'Plus Jakarta Sans'
      }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-outfit font-bold mb-2" style={{ color: activeTheme.text_color }}>
              Form Preview
            </h1>
            <p style={{ color: activeTheme.text_color, opacity: 0.7 }}>{campaignName}</p>
          </div>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="close-preview"
            className="border-slate-200 hover:bg-slate-50 font-jakarta rounded-lg"
          >
            <X className="w-4 h-4 mr-2" />
            Close Preview
          </Button>
        </div>

        {/* Header Image */}
        {headerImage && (
          <div className="mb-6 rounded-xl overflow-hidden shadow-lg">
            <img 
              src={headerImage} 
              alt="Form header" 
              className="w-full h-48 object-cover"
            />
          </div>
        )}

        {/* Form Card */}
        <Card 
          className="rounded-xl border shadow-card p-8"
          style={{ 
            backgroundColor: activeTheme.background_color,
            borderColor: activeTheme.card_style === 'glass' ? 'rgba(255,255,255,0.2)' : '#e2e8f0',
            backdropFilter: activeTheme.card_style === 'glass' ? 'blur(10px)' : 'none'
          }}
        >
          <form className="grid grid-cols-12 gap-6">
            {fields.map((field) => renderField(field))}
          </form>

          {/* Total Price */}
          {basePrice > 0 && (
            <div className="mt-8 pt-6 border-t" style={{ borderColor: `${activeTheme.text_color}20` }}>
              <div className="flex items-center justify-between">
                <span className="text-lg font-outfit font-semibold" style={{ color: activeTheme.text_color }}>
                  Total Amount
                </span>
                <span className="text-2xl font-outfit font-bold" style={{ color: activeTheme.primary_color }}>
                  ₹{calculateTotal().toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {/* Submit Button (Preview Only) */}
          <div className="mt-6">
            <Button
              type="button"
              disabled
              className="w-full font-jakarta font-medium rounded-lg h-12"
              style={{ 
                backgroundColor: activeTheme.primary_color,
                color: '#ffffff'
              }}
            >
              Register & Pay
            </Button>
            <p className="text-xs text-center mt-2" style={{ color: activeTheme.text_color, opacity: 0.5 }}>
              This is a preview - form submission is disabled
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default FormPreview;
